import type { ReactElement } from 'react';
import styled from 'styled-components';
import type { CopyCTA as CopyCTASchema } from '@amplience/content-types/typings/c-copy-cta';
import { ArrowLeft } from '@storefront/lib-global/icons';
import { LinkPartial } from '@storefront/lib-global/partials/link';
import { colours, maxWidthPartial, media, spacing } from '@storefront/lib-global/stylings';
import { getLocalizedValue } from '@storefront/lib-global/utils/transformers';

export type CopyCTAProps = CopyCTASchema;
const S = {
  CopyCTA: styled.div`
    background-color: ${colours.ALABASTER_WHITE};
    display: flex;
    width: 100%;
    @media ${media.greaterThan('lg')} {
      justify-content: center;
    }

    @media ${media.greaterThan('xx')} {
      ${maxWidthPartial({ maxWidth: 1300, withPadding: false })}
    }
  `,
  CopyWrapper: styled.div`
    background-color: ${colours.ALABASTER_WHITE};
    display: flex;
    flex-direction: column;
    gap: ${spacing.XXXS};
    justify-content: flex-start;
    padding: ${spacing.XS} 29px 10px 17px;
    width: 100%;
    @media ${media.greaterThan('lg')} {
      align-items: center;
      gap: ${spacing.M};
      justify-content: center;
      padding: ${spacing.M} 0;
    }
  `,

  CopyBlock: styled.span`
    font-size: 12px;
    font-weight: 500;
    line-height: 18px;
    text-align: left;
    @media ${media.greaterThan('lg')} {
      font-size: 16px;
      font-weight: 300;
      line-height: 24px;
      text-align: center;
    }
  `,

  CopyLinks: styled.ul<{ $itemSize: number }>`
    background-color: ${colours.ALABASTER_WHITE};
    display: grid;
    gap: ${spacing.XS};
    grid-template-columns: repeat(2, 1fr);
    margin: 0;
    padding: 0;
    width: 100%;
    @media ${media.greaterThan('lg')} {
      align-items: center;
      gap: 30px;
      grid-template-columns: ${({ $itemSize }) => `repeat(${$itemSize}, minmax(100px, 200px))`};
      justify-content: center;
    }
  `,
  CopyLinkItem: styled.li`
    margin: 0;
    padding: 0;

    &:hover,
    &:focus {
      svg {
        transform: translateX(4px);
      }
    }

    @media ${media.greaterThan('lg')} {
      display: grid;
      place-items: center;
    }
    a {
      color: ${colours.BLACK};
      text-decoration: none;
      @media ${media.greaterThan('lg')} {
        align-items: center;
      }
    }
  `,
  CopyText: styled.span<{ isLink?: boolean }>`
    align-items: center;
    display: flex;
    font-size: 12px;
    font-weight: 600;
    line-height: 18px;
    text-align: left;
    @media ${media.greaterThan('lg')} {
      border-bottom: ${({ isLink }) => (isLink ? `1px solid ${colours.BLACK}` : 'none')};
      font-size: 16px;
      font-weight: 300;
      line-height: 24px;
    }
  `,
};

function CopyLinks({ textLinks }: { textLinks: CopyCTAProps['textLinks'] }) {
  if (!textLinks) {
    return null;
  }

  return (
    <S.CopyLinks $itemSize={textLinks.length}>
      {textLinks.map(({ cta }) => {
        const { link, text } = cta;
        const localizedTextLink = getLocalizedValue(text);
        const isLink = !!(
          link.externalLink ||
          link.internalLink ||
          link.localizedExternalLink ||
          link.localizedInternalLink
        );
        return (
          <S.CopyLinkItem key={localizedTextLink}>
            {isLink ? (
              <LinkPartial ariaLabel={localizedTextLink} {...link}>
                <S.CopyText isLink={isLink}>
                  {localizedTextLink} <ArrowLeft />
                </S.CopyText>
              </LinkPartial>
            ) : (
              <S.CopyText>{localizedTextLink}</S.CopyText>
            )}
          </S.CopyLinkItem>
        );
      })}
    </S.CopyLinks>
  );
}

export function CopyCTA({ copyBlock, textLinks }: CopyCTAProps): ReactElement {
  const localizedCopyBlock = getLocalizedValue(copyBlock);
  return (
    <S.CopyCTA>
      <S.CopyWrapper>
        <S.CopyBlock>{localizedCopyBlock}</S.CopyBlock>
        <CopyLinks textLinks={textLinks} />
      </S.CopyWrapper>
    </S.CopyCTA>
  );
}

export default CopyCTA;
